import { setSmoother, gsap, ScrollSmoother, ScrollTrigger, SplitText, forceScrollSmootherUpdate, smoother, Flip, scrollPosition } from "./gsap";
import { documentReady, observeElementResize, scrollToEl } from "./utils";
import Dom from "../utility/dom";

// const body = document.querySelector('body');
const body = Dom.body;

let resizeRefreshTimeout;
window.addEventListener(
    "resize",
    () => {
        if (body.classList.contains("prevent-smoother-update")) return;

        clearTimeout(resizeRefreshTimeout);
        resizeRefreshTimeout = setTimeout(() => {
            // Resize mid-page can throw off ScrollTrigger measurements when
            // ScrollSmoother is enabled. Preserve scroll position while refreshing.
            forceScrollSmootherUpdate({ preserveScroll: true });
        }, 200);
    },
    { passive: true },
);

const InitializeSmoothing = () => {
    gsap.registerPlugin(ScrollTrigger);

    if (!!body.querySelector(".scroll-smoother-wrapper")) {
        // Register GSAP Plugins (comma separated) including:
        // ScrollTrigger, ScrollSmoother, SplitText, and Flip.
        gsap.registerPlugin(ScrollSmoother);

        ScrollTrigger.config({
            ignoreMobileResize: true,
            refreshPriority: 9,
        });

        documentReady(() => {
            observeElementResize(document.querySelector("#page"), () => {
                if (body.classList.contains("prevent-smoother-update")) return;

                forceScrollSmootherUpdate();
            });

            // If the page lands on a url with an anchor, update smooth scroll to
            // that position
            const hash = window.location.hash;
            const target = hash ? document.querySelector(hash) : null;
            if (!!target) {
                setTimeout(() => {
                    if (smoother) {
                        ScrollSmoother.get().scrollTo(target, true, "top 50px");
                    } else {
                        scrollToEl(target);
                    }
                }, 1000);
            }
        });

        //no scrollsmoother on mobile - too glitchy on ios
        if (!ScrollTrigger.isTouch) {
            body.classList.add("smoothscroll");

            setSmoother(
                ScrollSmoother.create({
                    wrapper: ".scroll-smoother-wrapper",
                    content: ".site",
                    smooth: 0.3,
                    ease: "power4.out",
                    effects: true,
                    ignoreMobileResize: true,
                    normalizeScroll: true,
                    smoothTouch: 0.1,
                }),
            );

            // This ensure's overflow:scroll works for nested content.
            ScrollTrigger.normalizeScroll({
                allowNestedScroll: true,
            });
        }
    }
};

export const watchAnimations = () => {
    const els = document.querySelectorAll('[class*="td_animation"]');

    if (els) {
        els.forEach((el) => {
            setAnimationScrollTrigger(el);
        });
    }
};

export const setAnimationScrollTrigger = (el) => {
    if (!el.classList.contains("animated") && el.animationSet == undefined) {
        el.animationSet = true;
        const anim = ScrollTrigger.create({
            trigger: el,
            start: "top bottom",
            end: "bottom top",
            onEnter: () => {
                // Media
                el.classList.add("animated");
                setTimeout(() => {
                    anim.kill(false);
                }, 2000);
            },
        });
    }
};

const InitializeSmoothAnchorLinks = () => {
    document.querySelectorAll("a[href^='#']").forEach((a) => {
        const linkHandler = (e) => {
            e.preventDefault();
            if (a.hash) {
                if ("preventDefault" in a.dataset) return;

                const target = document.querySelector(a.hash);
                if (!!target && !Dom.prefersReducedMotion) {
                    const targetPos = smoother ? smoother.offset(target, "top top") : target.getBoundingClientRect().top;

                    if (smoother) {
                        smoother.scrollTo(target, true, "top 50px");
                    } else {
                        scrollToEl(target);
                    }

                    // Hide the header for better landing.
                    const header = document.querySelector("#masthead");
                    header.classList.add("hide-while-scrolling");
                    setTimeout(() => {
                        header.classList.add("hide-header");
                        header.classList.remove("hide-while-scrolling");
                    }, 1100);
                }
            }
        };

        a.addEventListener("click", linkHandler);
    });
};

const IntializeHeaderState = () => {
    const header = document.querySelector("#masthead");
    if (!header) return;

    const hiddenClass = "hide-header";
    const scrolledClass = "header-scrolled";

    const applyHeaderState = (direction = 0) => {
        if (body.classList.contains("prevent-smoother-update")) return;

        // Always show header above 200px scroll position
        if (scrollPosition() < 200) {
            header.classList.remove(hiddenClass);
            body.classList.remove(scrolledClass);
            return;
        }

        body.classList.add(scrolledClass);

        // Only apply directional hide/show when we have a direction.
        if (direction === -1 && header.classList.contains(hiddenClass) && !body.classList.contains("preventHeaderReveal")) {
            header.classList.remove(hiddenClass);
        } else if (direction === 1 && !header.classList.contains(hiddenClass) && !body.classList.contains("preventHeaderHide")) {
            header.classList.add(hiddenClass);
        }
    };

    // Ensure initial state is correct (e.g. when landing mid-page)
    applyHeaderState(0);

    ScrollTrigger.create({
        trigger: body,
        start: "top top",
        end: "bottom bottom",
        onUpdate: (self) => applyHeaderState(self.direction),
        onRefresh: () => applyHeaderState(0),
    });
};

const InitTextDecoratorBlurAnimations = () => {
    const els = document.querySelectorAll(".is-blur-animation");
    els.forEach((el) => {
        // Use gsap split text to split each character into a span that will be animated
        const split = new SplitText(el, { type: "chars" });
        const chars = split.chars;
        const randomChars = [...chars].sort(() => Math.random() - 0.5);
        const tl = gsap.timeline({
            repeat: -1,
            repeatDelay: 0,
        });

        tl.set(chars, {
            opacity: 0.5,
            filter: "blur(0.22em)",
        });

        tl.to(randomChars, {
            opacity: 1,
            filter: "blur(0em)",
            duration: 2.25,
            ease: "power2.out",
            stagger: {
                each: 0.2,
            },
        });

        tl.to({}, { duration: 2.5 });

        tl.to(chars, {
            opacity: 0.5,
            filter: "blur(0.22em)",
            duration: 2,
            ease: "power2.out",
        });
    });
};

const ScrollLockGridProgressAnimation = () => {
    const scrollLockGrids = document.querySelectorAll(".scroll_lock_grid");
    if (scrollLockGrids.length === 0) return;

    scrollLockGrids.forEach((grid) => {
        ScrollTrigger.create({
            trigger: grid,
            start: "top 90%",
            end: "bottom 50%",
            onUpdate: (self) => {
                grid.style.setProperty("--scroll-progress", self.progress);
            },
        });
    });

    // Pin the media on desktop only (screens wider than 768px)
    ScrollTrigger.matchMedia({
        "(min-width: 769px)": () => {
            const topOffset = 100;

            scrollLockGrids.forEach((grid) => {
                const left = grid.querySelector(".scroll_lock_grid__left");
                const media = grid.querySelector(".scroll_lock_grid__media");
                if (!left || !media) return;

                ScrollTrigger.create({
                    // Emulate: position: sticky; top: 100px;
                    trigger: media,
                    start: () => `top top+=${topOffset}`,
                    endTrigger: left,
                    end: () => {
                        const dist = left.offsetHeight - media.offsetHeight;
                        return `+=${Math.max(1, dist)}`;
                    },
                    pin: media,
                    pinSpacing: false,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                });
            });
        },
    });
};

const InitializeGSAPOnPageLoad = () => {
    InitializeSmoothing();
    InitializeSmoothAnchorLinks();
    IntializeHeaderState();
    InitTextDecoratorBlurAnimations();
    ScrollLockGridProgressAnimation();

    forceScrollSmootherUpdate();

    window.addEventListener("load", (e) => {
        // Anchor links don't smooth scroll until the page has
        // been scrolled so we'll move the page 1px on load.
        const currentScroll = document.documentElement.scrollTop || document.body.scrollTop;
        scroll({
            top: currentScroll + 1,
            behavior: "smooth",
        });

        if (window.location.hash && smoother) {
            ScrollSmoother.get().scrollTo(window.location.hash, true);
        }
    });
};

export { gsap, InitializeGSAPOnPageLoad };
